import React, { useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import {
  Container,
  Grid,
  Typography,
  Button,
  Box,
  Paper,
  CircularProgress,
  Breadcrumbs,
  Link,
} from '@mui/material';
import { motion } from 'framer-motion';
import { useTranslation } from 'react-i18next';
import { typographyStyles } from '../styles/commonStyles';
import { fetchProductBySlug, clearCurrentProduct } from '../features/products/productsSlice';
import { addItem } from '../store/slices/cartSlice';
import WhatsAppButton from '../components/WhatsAppButton';

function ProductDetail() {
  const { slug } = useParams();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { t } = useTranslation();

  const { currentProduct: product, status, error } = useSelector((state) => state.products);

  useEffect(() => {
    dispatch(fetchProductBySlug(slug));
    return () => dispatch(clearCurrentProduct());
  }, [dispatch, slug]);

  const handleAddToCart = () => {
    if (product) {
      const cartItem = {
        id: product.id,
        name: product.name,
        price: parseFloat(product.price),
        quantity: 1,
        image_url: product.image_url
      };
      console.log('Adding product to cart:', cartItem);
      dispatch(addItem(cartItem));
      alert('Product added to cart!');
    }
  };

  if (status === 'loading') {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="60vh">
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="60vh">
        <Typography color="error">{error}</Typography>
      </Box>
    );
  }

  if (!product) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="60vh">
        <Typography>{t('products.notFound')}</Typography>
      </Box>
    );
  }

  return (
    <Container maxWidth="lg" sx={{ mt: 8, mb: 8 }}>
      <Breadcrumbs sx={{ mb: 3 }}>
        <Link
          component="button"
          variant="body1"
          onClick={() => navigate('/')}
          sx={{ cursor: 'pointer' }}
        >
          {t('common.home')}
        </Link>
        <Link
          component="button"
          variant="body1"
          onClick={() => navigate('/products')}
          sx={{ cursor: 'pointer' }}
        >
          {t('products.title')}
        </Link>
        <Typography color="text.primary">{product.name}</Typography>
      </Breadcrumbs>

      <Grid container spacing={4}>
        <Grid item xs={12} md={6}>
          <motion.div
            initial={{ opacity: 0, x: -20 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.5 }}
          >
            <Paper elevation={3}>
              <Box
                component="img"
                sx={{
                  width: '100%',
                  height: 'auto',
                  display: 'block',
                  borderRadius: '8px',
                  boxShadow: '0 4px 12px rgba(0,0,0,0.1)'
                }}
                src={product.image_url || 'https://via.placeholder.com/600'}
                alt={product.name}
              />
            </Paper>
          </motion.div>
        </Grid>

        <Grid item xs={12} md={6}>
          <motion.div
            initial={{ opacity: 0, x: 20 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.5 }}
          >
            <Typography
              component="h1"
              gutterBottom
              sx={typographyStyles.sectionTitle}
            >
              {product.name}
            </Typography>

            <Typography
              gutterBottom
              sx={typographyStyles.smallTitle}
            >
              ${product.price}
            </Typography>

            <Typography
              paragraph
              sx={typographyStyles.bodyText}
            >
              {product.description}
            </Typography>

            <Box sx={{ mt: 3 }}>
              <Typography
                gutterBottom
                sx={typographyStyles.smallTitle}
              >
                {t('products.stockStatus')}:{' '}
                <Box component="span" sx={{ color: product.stock > 0 ? 'success.main' : 'error.main' }}>
                  {product.stock > 0 ? t('products.inStock', { count: product.stock }) : t('products.outOfStock')}
                </Box>
              </Typography>
            </Box>

            <Button
              variant="contained"
              color="primary"
              size="large"
              onClick={handleAddToCart}
              disabled={product.stock === 0}
              sx={{ mt: 4 }}
              fullWidth
            >
              {product.stock === 0 ? t('products.outOfStock') : t('products.addToCart')}
            </Button>
          </motion.div>
        </Grid>
      </Grid>
    </Container>
  );
}

export default ProductDetail;
